/**
 * Privacy Policy Screen
 * 
 * Displays SuperJarvis privacy policy and SuperShelley consent controls.
 * GDPR-compliant with user data controls.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Trash2, Download, Check } from 'lucide-react';

type ConsentLevel = 'none' | 'minimal' | 'partial' | 'full';

const PrivacyScreen: React.FC = () => {
  const navigate = useNavigate();
  const [consentLevel, setConsentLevel] = useState<ConsentLevel>('full');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const consentOptions = [
    {
      level: 'none' as ConsentLevel,
      title: 'No Sharing',
      description: 'Your data stays completely private. No patterns shared with the Hive Mind.',
      icon: Lock,
    },
    {
      level: 'minimal' as ConsentLevel,
      title: 'Error Patterns Only',
      description: 'Share anonymized error patterns to help improve the system.',
      icon: Shield,
    },
    {
      level: 'partial' as ConsentLevel,
      title: 'Errors + Success',
      description: 'Share anonymized error and success patterns for better learning.',
      icon: Eye,
    },
    {
      level: 'full' as ConsentLevel,
      title: 'Full Contribution',
      description: 'Share all anonymized patterns. Help SuperJarvis learn faster for everyone.',
      icon: Check,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f] text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/30 border-b border-cyan-400/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl bg-cyan-400/10 hover:bg-cyan-400/20 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-cyan-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">
              Privacy Policy
            </h1>
            <p className="text-sm text-gray-400">Your data, your control</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* SuperShelley Consent */}
        <section className="bg-gradient-to-br from-cyan-400/5 to-purple-400/5 rounded-2xl border border-cyan-400/20 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-xl bg-purple-400/20">
              <Shield className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">SuperShelley Hive Mind</h2>
              <p className="text-sm text-gray-400">Control how your patterns help others</p>
            </div>
          </div>

          <div className="space-y-3">
            {consentOptions.map((option) => (
              <button
                key={option.level}
                onClick={() => setConsentLevel(option.level)}
                className={`w-full p-4 rounded-xl border transition-all text-left flex items-start gap-4 ${
                  consentLevel === option.level
                    ? 'bg-cyan-400/10 border-cyan-400/60 shadow-[0_0_20px_rgba(34,211,238,0.2)]'
                    : 'bg-white/5 border-white/10 hover:border-cyan-400/40'
                }`}
              >
                <div className={`p-2 rounded-lg ${consentLevel === option.level ? 'bg-cyan-400/20' : 'bg-white/10'}`}>
                  <option.icon className={`w-5 h-5 ${consentLevel === option.level ? 'text-cyan-400' : 'text-gray-400'}`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium">{option.title}</h3>
                  <p className="text-sm text-gray-400">{option.description}</p>
                </div>
                {consentLevel === option.level && (
                  <Check className="w-5 h-5 text-cyan-400" />
                )}
              </button>
            ))}
          </div>

          <p className="mt-4 text-xs text-gray-500">
            All shared data is anonymized using one-way hashing. Your actual content is never shared.
          </p>
        </section>

        {/* Privacy Policy Text */}
        <section className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-6">
          <h2 className="text-lg font-semibold">Privacy Policy</h2>
          
          <div className="space-y-4 text-sm text-gray-300">
            <div>
              <h3 className="font-medium text-white mb-2">1. Data Collection</h3>
              <p>SuperJarvis collects data necessary to provide AI assistant services, including:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                <li>Account information (email, name)</li>
                <li>Conversation history with the AI</li>
                <li>Task and project data you create</li>
                <li>Connected app permissions and tokens</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">2. How We Use Your Data</h3>
              <p>Your data is used to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                <li>Provide personalized AI assistance</li>
                <li>Execute tasks on your behalf</li>
                <li>Improve our AI through anonymized patterns (with consent)</li>
                <li>Maintain and secure your account</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">3. SuperShelley Hive Mind</h3>
              <p>With your consent, anonymized interaction patterns may be shared to improve SuperJarvis for everyone. Key points:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                <li>All data is one-way hashed — we can't reverse it</li>
                <li>No personal information is ever shared</li>
                <li>You can opt out at any time</li>
                <li>You can request deletion of your contributions</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">4. Data Security</h3>
              <p>We protect your data using:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                <li>End-to-end encryption for sensitive data</li>
                <li>PII scrubbing before any processing</li>
                <li>Secure cloud infrastructure (Google Cloud)</li>
                <li>Regular security audits</li>
              </ul>
            </div>

            <div>
              <h3 className="font-medium text-white mb-2">5. Your Rights (GDPR)</h3>
              <p>You have the right to:</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-gray-400">
                <li>Access your data</li>
                <li>Correct inaccurate data</li>
                <li>Delete your data</li>
                <li>Export your data</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Data Actions */}
        <section className="bg-white/5 rounded-2xl border border-white/10 p-6 space-y-4">
          <h2 className="text-lg font-semibold">Your Data</h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button className="flex items-center gap-3 p-4 rounded-xl bg-cyan-400/10 border border-cyan-400/30 hover:bg-cyan-400/20 transition-colors">
              <Download className="w-5 h-5 text-cyan-400" />
              <div className="text-left">
                <h3 className="font-medium">Export Data</h3>
                <p className="text-xs text-gray-400">Download all your data</p>
              </div>
            </button>
            
            <button 
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-3 p-4 rounded-xl bg-red-400/10 border border-red-400/30 hover:bg-red-400/20 transition-colors"
            >
              <Trash2 className="w-5 h-5 text-red-400" />
              <div className="text-left">
                <h3 className="font-medium text-red-400">Delete Data</h3>
                <p className="text-xs text-gray-400">Remove all your data</p>
              </div>
            </button>
          </div>
        </section>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-[#1a1a2e] rounded-2xl border border-red-400/30 p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold text-red-400 mb-2">Delete All Data?</h3>
              <p className="text-sm text-gray-400 mb-6">
                This will permanently delete your account, all conversations, tasks, projects, and apps. This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
                >
                  Cancel
                </button>
                <button className="flex-1 py-2 rounded-xl bg-red-500 hover:bg-red-600 transition-colors">
                  Delete Everything
                </button>
              </div>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-gray-500">
          Last updated: January 31, 2025 • Contact: privacy@superjarvis.ai
        </p>
      </main>
    </div>
  );
};

export default PrivacyScreen;
